import * as React from "react";

import { consumeAgentDraft } from "@/shared/domain/agents";

import { draftToFormState, type FormState } from "./buildRequestTemplate";

const EMPTY_FORM: FormState = {
  name: "",
  description: "",
  modelProfile: null,
  kickoffMessage: "",
  goals: [],
  variableMeta: {},
};

/**
 * Claim any pending `client_create_agent` draft and return a
 * `useState`-shaped `[form, setForm]` tuple seeded from it.
 *
 * Edit-mode skips the consume so a stale tool invocation can't silently
 * overwrite a saved agent's values — those arrive via the `useAgent`
 * query and are seeded by the effect in AgentEditor itself.
 *
 * Idempotency under React 18 StrictMode is owned by
 * {@link consumeAgentDraft}, not by this hook: the underlying cache
 * preserves the draft across StrictMode's synthetic
 * mount → unmount → remount cycle so both invocations of this
 * initializer see the same value. See `agentDraft.ts` for the cache
 * lifecycle.
 */
export function useDraftSeededForm(
  mode: "create" | "edit",
): [FormState, React.Dispatch<React.SetStateAction<FormState>>] {
  return React.useState<FormState>(() => {
    if (mode !== "create") {
      return EMPTY_FORM;
    }
    const draft = consumeAgentDraft();
    return draft === null ? EMPTY_FORM : draftToFormState(draft);
  });
}
